import React, { useMemo, useState } from 'react';
import SEO from '../components/SEO';
import { PageContainer, PageTitle } from '../components/PageHeader';
import { useGScoreDashboard } from '../features/cognition/hooks/useGScoreDashboard';
import { useAssessmentBattery } from '../features/cognition/hooks/useAssessmentBattery';
import { useGScoreHistory } from '../features/cognition/hooks/useGScoreHistory';
import ScoreHero from '../features/cognition/components/ScoreHero';
import PillarCard from '../features/cognition/components/PillarCard';
import NextAssessmentBanner from '../features/cognition/components/NextAssessmentBanner';
import StreakStrip from '../features/cognition/components/StreakStrip';
import GameCard from '../features/cognition/components/GameCard';
import ScoreHistoryModal from '../features/cognition/components/ScoreHistoryModal';
import { Skeleton, SkeletonPanel, SkeletonRegion } from '../components/ui/Skeleton';
import ErrorBlock from '../features/parentPortal/components/ErrorBlock';
import { humanDuration } from '../features/cognition/utils/format';
import {
  orderedPillars,
  runnableRoutineGames,
  startablePillarIds,
} from '../features/cognition/utils/dashboard';
import '../styles/gscore.css';

export default function GScorePage() {
  const { data, loading, error, reload } = useGScoreDashboard();
  // Startability is a battery question, not a dashboard question, so a failed
  // battery load does not take the whole page down — the scores still render.
  // But it cannot be swallowed either: startablePillarIds() answers "empty" for
  // loading, failed, and genuinely-nothing-startable alike, and reporting
  // "0 of 6 pillars can be assessed today" with every card inert is a lie when
  // the truth is that we never got an answer. Hence loading/error/reload.
  const {
    data: battery,
    loading: batteryLoading,
    error: batteryError,
    reload: reloadBattery,
  } = useAssessmentBattery();
  // Drives both the hero's sparkline and its "Last assessed <date>" line —
  // the immutable history log is the only honest source for either, since
  // the dashboard response has no top-level composite date. Always enabled
  // (not tied to modal visibility) because the sparkline is on-page, not
  // behind a click; ScoreHistoryModal fetches its own copy when opened.
  const history = useGScoreHistory({ enabled: true });
  const [historyOpen, setHistoryOpen] = useState(false);
  const pillars = orderedPillars(data);
  const routineGames = runnableRoutineGames(data);
  const startable = startablePillarIds(battery);

  const historyEntries = history.data?.entries;
  const lastAssessedAt = useMemo(() => {
    if (!Array.isArray(historyEntries) || historyEntries.length === 0) return null;
    // Entries arrive oldest -> newest.
    return historyEntries[historyEntries.length - 1]?.takenAt ?? null;
  }, [historyEntries]);

  const routineTotalSeconds = routineGames.reduce(
    (sum, g) => sum + (typeof g.durationSeconds === 'number' ? g.durationSeconds : 0),
    0
  );
  const routineMeta =
    routineGames.length > 0
      ? `${routineGames.length} session${routineGames.length === 1 ? '' : 's'} · about ${humanDuration(
          routineTotalSeconds
        )} total`
      : null;

  return (
    <>
      <SEO title="G-Score" description="Your cognitive fitness score on Graspd." path="/g-score" noindex />
      <PageContainer>
        <PageTitle style={{ marginBottom: 6 }}>G-Score</PageTitle>
        <p
          style={{
            margin: '0 0 24px',
            fontSize: 14.5,
            lineHeight: 1.5,
            color: 'var(--text-secondary)',
            maxWidth: 660,
          }}
        >
          A measure of cognitive strength across six pillars. It moves only when you take an
          assessment, and it is always compared with your own baseline.
        </p>

        {loading ? (
          /* Mirrors the real layout below: hero, then the six-across pillar
             row, then the four-across routine row. The pillar skeleton uses
             the same .gscore-pillar-grid class as the content it stands in
             for, so the row can't reflow when the data lands. */
          <SkeletonRegion label="Loading your G-Score">
            <SkeletonPanel style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }}>
              <div style={{ flex: 'none' }}>
                <Skeleton width={92} height={44} style={{ marginBottom: 'var(--space-2)' }} />
                <Skeleton width={120} height={12} />
              </div>
              <Skeleton height={70} style={{ flex: 1 }} />
            </SkeletonPanel>

            <Skeleton width="26%" height={18} style={{ marginBottom: 'var(--space-4)' }} />
            <div className="gscore-pillar-grid" style={{ marginBottom: 'var(--space-6)' }}>
              {Array.from({ length: 6 }, (_, i) => (
                <SkeletonPanel key={i}>
                  <Skeleton height={32} style={{ marginBottom: 'var(--space-3)' }} />
                  <Skeleton width="46%" height={27} style={{ marginBottom: 'var(--space-3)' }} />
                  <Skeleton height={5} style={{ marginBottom: 'var(--space-2)' }} />
                  <Skeleton width="62%" height={11} />
                </SkeletonPanel>
              ))}
            </div>

            <Skeleton width="22%" height={18} style={{ marginBottom: 'var(--space-4)' }} />
            <div className="gscore-routine-grid">
              {Array.from({ length: 4 }, (_, i) => (
                <SkeletonPanel key={i}>
                  <Skeleton width="52%" height={11} style={{ marginBottom: 'var(--space-3)' }} />
                  <Skeleton width="70%" height={15} style={{ marginBottom: 'var(--space-2)' }} />
                  <Skeleton width="58%" height={12} style={{ marginBottom: 'var(--space-4)' }} />
                  <Skeleton height={34} />
                </SkeletonPanel>
              ))}
            </div>
          </SkeletonRegion>
        ) : error ? (
          <ErrorBlock message={error} onRetry={reload} />
        ) : !data ? null : (
          // Each piece below (ScoreHero, NextAssessmentBanner, the section
          // wrappers) carries its own bottom margin matching the mockup's
          // per-element spacing, so this container adds none of its own —
          // a `gap` here would double up on top of those margins.
          <div>
            <ScoreHero
              score={data.composite}
              lastAssessedAt={lastAssessedAt}
              historyEntries={historyEntries}
              historyLoading={history.loading}
              historyError={history.error}
              onViewHistory={() => setHistoryOpen(true)}
            />

            {(() => {
              const due = pillars.find((p) => startable.has(p.id) && p.assessment?.overdue);
              return due ? (
                <NextAssessmentBanner assessment={due.assessment} to={`/g-score/assessment/${due.id}`} />
              ) : null;
            })()}

            <section style={{ marginBottom: 38 }}>
              <SectionHeading
                title="Cognitive pillars"
                // Both numbers must come from the same authority: the pillars
                // actually rendered on this screen (orderedPillars), filtered
                // to the ones the battery says are startable. `startable.size`
                // alone is the battery's total count, which can include a
                // pillar this screen never rendered at all — the two lists
                // have already disagreed once (see dashboard.js). Counting
                // that phantom pillar in the numerator produces "7 of 6".
                // Only claimable once the battery has actually answered. While
                // it is loading, or after it failed, the count would read
                // "0 of N" — indistinguishable from a real answer.
                meta={
                  batteryLoading || batteryError
                    ? null
                    : `${pillars.filter((p) => startable.has(p.id)).length} of ${pillars.length} pillars can be assessed today.`
                }
              />
              {batteryError && (
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <ErrorBlock message={batteryError} onRetry={reloadBattery} />
                </div>
              )}
              <div
                className="gscore-pillar-grid"
                // Six pillars, one row — the row only widens if the backend
                // ever surfaces more than six, never narrows to fewer (a
                // one-pillar payload would otherwise render one 1112px card).
                // String, not number: a bare number on a custom property is
                // the one case React can still suffix with `px`.
                style={{ '--gscore-pillar-columns': String(Math.max(pillars.length, 6)) }}
              >
                {pillars.map((p) => (
                  <PillarCard
                    key={p.id}
                    pillar={p}
                    to={startable.has(p.id) ? `/g-score/assessment/${p.id}` : null}
                  />
                ))}
              </div>
            </section>

            {routineGames.length > 0 && (
              <section style={{ marginBottom: 'var(--space-5)' }}>
                <SectionHeading
                  title="Today's routine"
                  subtitle="Short adaptive sessions. Training keeps the routine — assessments produce the score."
                  meta={routineMeta}
                />
                <div className="gscore-routine-grid">
                  {routineGames.map((g) => (
                    <GameCard
                      key={g.id}
                      recommendation={g}
                      completed={data.todayRoutine?.completedIds?.includes(g.id) === true}
                    />
                  ))}
                </div>
              </section>
            )}

            <StreakStrip streak={data.streak} />
          </div>
        )}
      </PageContainer>

      <ScoreHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
}

function SectionHeading({ title, subtitle, meta }) {
  return (
    <div style={{ margin: '0 0 var(--space-4)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          // The meta line is nowrap so it reads as one statement; wrapping the
          // row is what keeps it from colliding with the heading on narrow
          // screens instead of overflowing the container.
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--text-section-size)',
            fontWeight: 'var(--text-section-weight)',
            letterSpacing: 'var(--text-section-tracking)',
            color: 'var(--text-primary)',
          }}
        >
          {title}
        </h2>
        {meta && (
          <span
            style={{
              fontSize: 'var(--text-numeric-size)',
              color: 'var(--text-tertiary)',
              whiteSpace: 'nowrap',
            }}
          >
            {meta}
          </span>
        )}
      </div>
      {subtitle && (
        <p
          style={{
            margin: 'var(--space-1) 0 0',
            fontSize: 'var(--text-body-size)',
            lineHeight: 1.5,
            color: 'var(--text-secondary)',
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}
